import React, { useMemo } from 'react';

import { LayoutChangeEvent, useWindowDimensions } from 'react-native';

import { DeliveryFeesStripe } from 'components/delivery-fees-stripe';
import { useIsDesktopBreakpoint } from 'hooks/use-media-query';
import { MenuAcceleratorBadgePDP } from 'pages/menu/components/menu-accelerator-badge/menu-accelerator-badge-pdp';
import { useAcceleratorBadgesOnMenu } from 'pages/menu/hooks/use-accelerator-badges-on-menu';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useProductWizardContext } from 'state/product-wizard-v2';
import { useScrollResetOnMount } from 'state/scroll/hooks';
import { hasExcessSodium } from 'utils/menu/sodium';
import { getSelectionsFromMenuData } from 'utils/wizard';

import { SodiumFlag } from '../nutrition-information';
import { PriceAndCalories } from '../price-and-calories';

import { MOBILE_SAFETY_MEASURE } from './constants';
import { CurvedBackground } from './curved-bg';
import { ProductImageWrapper, ProductInformationContainer, StyledProductPicture } from './styled';

const sodiumFlagIcon = <SodiumFlag size={{ base: '20px', md: '26px' }} />;

export const ProductOverview: React.FC<React.PropsWithChildren<{ onImageLoaded?: () => void }>> = ({
  onImageLoaded,
}) => {
  const [bgHeight, setBgHeight] = React.useState<number>(0);
  const { menuObject, selectedProduct, userSelections } = useProductWizardContext();

  useScrollResetOnMount();

  const {
    experimentEnabled,
    shouldShowMenuObjectBadge,
    bonusPoints,
    pointsMultiplier,
  } = useAcceleratorBadgesOnMenu({
    skipExposureLogging: false,
    menuData: [menuObject],
    isFirstPickerAspect: true,
  });

  const isDesktop = useIsDesktopBreakpoint();
  const { height } = useWindowDimensions();

  const forcePickerImage = useFlag(LaunchDarklyFlag.ENABLE_FORCE_PICKER_IMAGE);

  const isOverSodiumLimit = useMemo(() => {
    const { comboSlotSelections, modifierSelections } = getSelectionsFromMenuData({
      data: menuObject,
      pickerSelectionOverrides: userSelections.pickerAspects,
    });

    return hasExcessSodium({
      selectedOpt: selectedProduct,
      comboSlotSelections,
      modifierSelections,
    });
  }, [menuObject, selectedProduct, userSelections.pickerAspects]);

  const image = forcePickerImage ? menuObject.image : selectedProduct.image;

  return (
    <>
      <ProductImageWrapper>
        <CurvedBackground height={bgHeight} />

        <StyledProductPicture
          key={image?.asset?._id ?? 'product-image'}
          image={image}
          alt={menuObject?.imageDescription?.locale || ''}
          width={height / (isDesktop ? 2 : 2.5)}
          onImageLoaded={onImageLoaded}
          onLayout={({ nativeEvent }: LayoutChangeEvent) => {
            const { height } = nativeEvent.layout;
            // Sets the CurvedMayoBgColor height to be: (img height - 40% of the img height)
            // We will be updating this logic base on design needs, leaving this calculation for now.
            // The Main idea here is to have the background svg curved effect.
            const bgHeight = height - height * 0.4;
            setBgHeight(bgHeight);
          }}
          {...(!isDesktop && { minHeight: MOBILE_SAFETY_MEASURE })}
        />

        {experimentEnabled && shouldShowMenuObjectBadge && !!(bonusPoints || pointsMultiplier) && (
          <MenuAcceleratorBadgePDP bonusPoints={bonusPoints} pointsMultiplier={pointsMultiplier} />
        )}
      </ProductImageWrapper>

      <ProductInformationContainer>
        <PriceAndCalories
          productPriceConditions={{
            shouldIncludeModifications: true,
            shouldIncludeQuantity: false,
          }}
          icon={isOverSodiumLimit ? sodiumFlagIcon : undefined}
        />

        {/* @TODO: Do we want to keep this in here ? */}
        <DeliveryFeesStripe />
      </ProductInformationContainer>
    </>
  );
};
